import { useState, useEffect } from 'react';
import { SectionWrapper } from '../SectionWrapper';
import { CreditCard, Check, Copy, Info } from 'lucide-react';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import { getIntegrations } from '@/data/supabase/integrations';
import doordashLogo from '@/assets/doordash-logo.png';
import uberEatsLogo from '@/assets/uber-eats-logo.svg';
import toastLogo from '@/assets/toast-logo.png';
import squareLogo from '@/assets/square-logo.png';
import cloverLogo from '@/assets/clover-POS.svg';
import revelLogo from '@/assets/revel-logo.svg';
import touchbistroLogo from '@/assets/touchbistro-logo.svg';
import lightspeedLogo from '@/assets/lightspeed-logo.png';
import resyLogo from '@/assets/resy-logo.png';
import opentableLogo from '@/assets/opentable-logo.png';
import otherPosLogo from '@/assets/other-pos-logo.svg';

interface POSPlatform {
  id: string;
  name: string;
  logo: string;
  description: string;
  supported: boolean;
}

const POS_PLATFORMS: POSPlatform[] = [
  {
    id: 'toast',
    name: 'Toast',
    logo: toastLogo,
    description: 'Cloud-based POS system for restaurants',
    supported: true,
  },
  {
    id: 'doordash',
    name: 'DoorDash',
    logo: doordashLogo,
    description: 'Food delivery and sales platform',
    supported: true,
  },
  {
    id: 'ubereats',
    name: 'Uber Eats',
    logo: uberEatsLogo,
    description: 'Food delivery and sales platform',
    supported: true,
  },
  {
    id: 'square',
    name: 'Square',
    logo: squareLogo,
    description: 'Point of sale and payment processing',
    supported: true,
  },
  {
    id: 'clover',
    name: 'Clover',
    logo: cloverLogo,
    description: 'All-in-one POS and payment platform',
    supported: true,
  },
  {
    id: 'revel',
    name: 'Revel Systems',
    logo: revelLogo,
    description: 'iPad POS system for restaurants',
    supported: true,
  },
  {
    id: 'touchbistro',
    name: 'TouchBistro',
    logo: touchbistroLogo,
    description: 'Restaurant POS and management system',
    supported: true,
  },
  {
    id: 'lightspeed',
    name: 'Lightspeed',
    logo: lightspeedLogo,
    description: 'Cloud-based restaurant POS',
    supported: true,
  },
  {
    id: 'resy',
    name: 'Resy',
    logo: resyLogo,
    description: 'Restaurant reservation and POS',
    supported: false,
  },
  {
    id: 'opentable',
    name: 'OpenTable',
    logo: opentableLogo,
    description: 'Restaurant reservations and guest management',
    supported: false,
  },
  {
    id: 'other',
    name: 'Other POS System',
    logo: otherPosLogo,
    description: 'Using a different POS system',
    supported: false,
  },
];

interface POSIntegrationSectionProps {
  /** When provided (e.g. from POS training flow step 1), called after user completes setup so parent can advance to next step */
  onCompleteAndAdvance?: () => void;
}

export const POSIntegrationSection = ({ onCompleteAndAdvance }: POSIntegrationSectionProps) => {
  const { completeSection, skipSection, state, updateSectionData } = useOnboarding();
  const { toast } = useToast();
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [customPlatforms, setCustomPlatforms] = useState<Record<string, string>>({}); // platformId -> custom name
  const [platformDetails, setPlatformDetails] = useState<Record<string, {
    platformType?: string;
    payoutFrequency?: string;
    payoutWeekday?: string; // Day of week for weekly payouts
    integrationConfirmed?: boolean; // Confirmation that API/email has been added to platform
  }>>({});
  const [authorizationSettings, setAuthorizationSettings] = useState<Record<string, {
    authorizedEmail?: string;
    integrationMethod?: 'integrated' | 'agent_based';
    apiKey?: string;
    apiSecret?: string;
  }>>({});
  const [integrationEmails, setIntegrationEmails] = useState<Record<string, string>>({});
  
  // Wizard state - one platform at a time
  const [currentStep, setCurrentStep] = useState<'select' | 'configure'>('select');
  const [currentPlatformIndex, setCurrentPlatformIndex] = useState(0);

  // Get current platform being configured (computed values)
  const currentPlatformId = selectedPlatforms[currentPlatformIndex];
  const currentPlatform = currentPlatformId ? POS_PLATFORMS.find(p => p.id === currentPlatformId) : null;
  const currentDisplayName = currentPlatformId === 'other' 
    ? (customPlatforms['other'] || 'Other POS System')
    : (currentPlatform?.name || currentPlatformId);

  // Fetch integration emails on mount and generate for selected platforms
  useEffect(() => {
    const fetchIntegrationEmails = async () => {
      try {
        const companyId = await getCurrentUserCompanyId();
        if (!companyId) return;
        const { data: companyData, error: companyError } = await supabase
          .from("companies")
          .select("integration_email")
          .eq("id", companyId)
          .single();

        if (companyError) throw companyError;

        if (companyData?.integration_email) {
          const integrationEmailData = companyData.integration_email as Record<string, string> | null;
          if (integrationEmailData) {
            // Get all integration emails (not just doordash and ubereats)
            setIntegrationEmails(integrationEmailData);
          }
        }
      } catch (error) {
        console.error("Error fetching integration emails:", error);
      }
    };

    fetchIntegrationEmails();
  }, []);

  // Generate integration email for a platform if it doesn't exist
  const getOrGenerateIntegrationEmail = async (platformId: string): Promise<string | null> => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return null;
      
      // Check if we already have the email in state
      if (integrationEmails[platformId]) {
        return integrationEmails[platformId];
      }

      // Call RPC function to get or generate email
      const { data, error } = await supabase.rpc('get_or_generate_integration_email', {
        p_company_id: companyId,
        p_platform_id: platformId
      });

      if (error) throw error;

      if (data) {
        // Update state with the new email
        setIntegrationEmails(prev => ({
          ...prev,
          [platformId]: data
        }));
        return data;
      }

      return null;
    } catch (error) {
      console.error(`Error generating integration email for ${platformId}:`, error);
      return null;
    }
  };

  // Load saved selections from onboarding progress, with fallback to company_integrations
  useEffect(() => {
    const savedData = state.sections?.pos_integration?.data;
    const hasSavedPlatforms = Array.isArray(savedData?.selectedPlatforms) && savedData.selectedPlatforms.length > 0;

    if (hasSavedPlatforms) {
      setSelectedPlatforms(savedData!.selectedPlatforms);
      if (savedData!.customPlatforms) setCustomPlatforms(savedData!.customPlatforms);
      if (savedData!.platformDetails) setPlatformDetails(savedData!.platformDetails);
      if (savedData!.authorizationSettings) setAuthorizationSettings(savedData!.authorizationSettings);
      return;
    }

    // Fallback: pre-populate from company_integrations (e.g. user saved via "Next" earlier)
    const loadFromIntegrations = async () => {
      try {
        const companyId = await getCurrentUserCompanyId();
        if (!companyId) return;
        const integrations = await getIntegrations(companyId);
        const platformIds = integrations
          .filter((i) => i.is_active)
          .map((i) => i.integration_type)
          .filter((type) => POS_PLATFORMS.some((p) => p.id === type));
        if (platformIds.length > 0) {
          setSelectedPlatforms(platformIds);
        }
      } catch (e) {
        console.error('Error loading POS selections from company_integrations:', e);
      }
    };
    loadFromIntegrations();
  }, [state.sections]);

  // Auto-generate and populate integration email when platform is selected and configured
  useEffect(() => {
    const autoPopulateEmail = async () => {
      if (currentStep === 'configure' && currentPlatformId && currentPlatformId !== 'other') {
        // Generate email if it doesn't exist
        const email = await getOrGenerateIntegrationEmail(currentPlatformId);
        
        if (email) {
          // Auto-populate authorized email if it's empty using functional updates
          setAuthorizationSettings(prev => {
            const currentAuthSettings = prev[currentPlatformId] || {};
            if (!currentAuthSettings.authorizedEmail) {
              const updated = {
                ...prev,
                [currentPlatformId]: { ...prev[currentPlatformId], authorizedEmail: email }
              };
              
              // Update onboarding data
              updateSectionData('pos_integration', {
                selectedPlatforms,
                customPlatforms,
                platformDetails,
                authorizationSettings: updated
              });
              
              return updated;
            }
            return prev;
          });
        }
      }
    };

    autoPopulateEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, currentPlatformId, currentPlatformIndex]);

  const togglePlatform = (platformId: string) => {
    setSelectedPlatforms(prev => {
      const updated = prev.includes(platformId)
        ? prev.filter(id => id !== platformId)
        : [...prev, platformId];
      
      // Auto-save to onboarding progress
      updateSectionData('pos_integration', {
        selectedPlatforms: updated,
        customPlatforms,
        platformDetails,
        platforms: updated.map(id => {
          const platform = POS_PLATFORMS.find(p => p.id === id);
          return {
            id,
            name: platform?.id === 'other' ? (customPlatforms[id] || 'Other POS System') : (platform?.name || id),
            supported: platform?.supported || false,
          };
        }),
      });
      
      return updated;
    });
  };

  const handleNext = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) throw new Error('Company ID not found');
      
      // Map platform IDs to integration types that match the database constraint
      const integrationTypeMap: Record<string, string> = {
        'toast': 'toast',
        'doordash': 'doordash',
        'ubereats': 'ubereats',
      };

      // Save supported platforms to company_integrations table
      if (selectedPlatforms.length > 0) {
        const supportedPlatforms = selectedPlatforms.filter(id => integrationTypeMap[id]);
        
        for (const platformId of supportedPlatforms) {
          const integrationType = integrationTypeMap[platformId];
          const platform = POS_PLATFORMS.find(p => p.id === platformId);
          
          const { error } = await supabase
            .from('company_integrations')
            .upsert({
              company_id: companyId,
              integration_type: integrationType,
              config: {
                platform_id: platformId,
                platform_name: platform?.name || platformId,
                selected_during_onboarding: true,
                supported: platform?.supported || false,
              },
              is_active: true,
            }, {
              onConflict: 'company_id,integration_type'
            });

          if (error) {
            console.error(`Error saving integration for ${integrationType}:`, error);
            // Continue with other platforms even if one fails
          }
        }
      }

      // Save all selections (including unsupported) to onboarding progress
      const platformsData = selectedPlatforms.map(id => {
        const platform = POS_PLATFORMS.find(p => p.id === id);
        return {
          id,
          name: id === 'other' ? (customPlatforms['other'] || 'Other POS System') : (platform?.name || id),
          supported: platform?.supported || false,
          details: platformDetails[id] || {},
        };
      });

      // Save platform details and authorization to company_integrations config
      for (const platformId of selectedPlatforms) {
        const integrationTypeMap: Record<string, string> = {
          'toast': 'toast',
          'doordash': 'doordash',
          'ubereats': 'ubereats',
        };
        const integrationType = integrationTypeMap[platformId];
        if (integrationType) {
          const config: any = {
            platform_id: platformId,
            ...platformDetails[platformId],
            ...authorizationSettings[platformId],
          };
          
          const { error } = await supabase
            .from('company_integrations')
            .update({
              config,
            })
            .eq('company_id', companyId)
            .eq('integration_type', integrationType);
          
          if (error) {
            console.error(`Error updating platform details for ${integrationType}:`, error);
          }
        }
      }

      completeSection('pos_integration', {
        selectedPlatforms,
        customPlatforms,
        platformDetails,
        authorizationSettings,
        platforms: platformsData,
        totalSelected: selectedPlatforms.length,
        supportedCount: platformsData.filter(p => p.supported).length,
      });

      toast({
        title: 'POS Platforms Saved',
        description: `${selectedPlatforms.length} platform(s) selected`
      });

      onCompleteAndAdvance?.();
    } catch (error) {
      console.error('Error saving POS platforms:', error);
      toast({
        title: 'Error',
        description: 'Failed to save POS platform selection',
        variant: 'destructive'
      });
    }
  };

  const handleContinueToConfiguration = () => {
    if (selectedPlatforms.length === 0) {
      toast({
        title: 'No platforms selected',
        description: 'Please select at least one POS platform to continue.',
        variant: 'destructive'
      });
      return;
    }
    setCurrentStep('configure');
    setCurrentPlatformIndex(0);
  };

  const handleNextPlatform = () => {
    // Check if confirmation is required for current platform
    const requiresConfirmation = ['toast', 'doordash', 'ubereats'].includes(currentPlatformId) && integrationEmails[currentPlatformId];
    const isConfirmed = platformDetails[currentPlatformId]?.integrationConfirmed || false;
    
    if (requiresConfirmation && !isConfirmed) {
      toast({
        title: 'Confirmation Required',
        description: `Please confirm that you have completed the ${currentDisplayName} authorization setup before continuing.`,
        variant: 'destructive'
      });
      return;
    }

    if (currentPlatformIndex < selectedPlatforms.length - 1) {
      setCurrentPlatformIndex(currentPlatformIndex + 1);
    } else {
      // All platforms configured, proceed to save
      handleNext();
    }
  };

  const handleSkipPlatform = () => {
    if (currentPlatformIndex < selectedPlatforms.length - 1) {
      setCurrentPlatformIndex(currentPlatformIndex + 1);
    } else {
      handleNext();
    }
  };

  const handleBackToSelection = () => {
    setCurrentStep('select');
  };

  const handleSave = async () => {
    try {
      // Save current progress to onboarding_progress
      await updateSectionData('pos_integration', {
        selectedPlatforms,
        customPlatforms,
        platformDetails,
        authorizationSettings,
        integrationEmails,
        platforms: selectedPlatforms.map(id => {
          const platform = POS_PLATFORMS.find(p => p.id === id);
          return {
            id,
            name: platform?.id === 'other' ? (customPlatforms[id] || 'Other POS System') : (platform?.name || id),
            supported: platform?.supported || false,
          };
        }),
      });
      
      toast({
        title: 'Success',
        description: 'POS integration saved successfully',
      });
    } catch (error: any) {
      console.error('Error saving POS integration:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save POS integration',
        variant: 'destructive'
      });
    }
  };

  return (
    <SectionWrapper
      title="POS Integration & Data Enablement"
      description="Select your POS platform(s) to enable automated data sync. This helps us import sales, menu items, and transaction data automatically."
      icon={<CreditCard className="h-6 w-6" />}
      onNext={currentStep === 'select' ? handleContinueToConfiguration : handleNext}
      onSave={handleSave}
      showSaveButton={true}
      onSkip={() => skipSection('pos_integration')}
      canSkip
    >
      {currentStep === 'select' ? (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Platform Selection</CardTitle>
              <CardDescription>
                Choose the POS system(s) you use. You'll configure each one individually next.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {POS_PLATFORMS.map((platform) => {
                  const isSelected = selectedPlatforms.includes(platform.id);
                  const isSupported = platform.supported;

                  return (
                    <button
                      key={platform.id}
                      onClick={() => togglePlatform(platform.id)}
                      className={cn(
                        "relative p-4 rounded-lg border-2 transition-all text-left",
                        "hover:border-primary/50 hover:shadow-md",
                        isSelected
                          ? "border-primary bg-primary/5"
                          : "border-muted-foreground/25",
                        !isSupported && "opacity-60"
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <img
                            src={platform.logo}
                            alt={platform.name}
                            className="h-12 w-12 object-contain rounded"
                            onError={(e) => {
                              // Hide image if it fails to load
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-sm">{platform.name}</h3>
                            {isSelected && (
                              <Check className="h-4 w-4 text-primary flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {platform.description}
                          </p>
                          {!isSupported && (
                            <span className="inline-block mt-2 text-xs text-muted-foreground">
                              Coming soon
                            </span>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <div className="absolute top-2 right-2">
                          <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                            <Check className="h-3 w-3 text-primary-foreground" />
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Custom platform input for "Other" */}
          {selectedPlatforms.includes('other') && (
            <Card>
              <CardHeader>
                <CardTitle>Custom POS Platform</CardTitle>
                <CardDescription>
                  Please enter the name of your POS platform
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label htmlFor="custom-platform">POS Platform Name</Label>
                  <Input
                    id="custom-platform"
                    value={customPlatforms['other'] || ''}
                    onChange={(e) => {
                      const updated = { ...customPlatforms, 'other': e.target.value };
                      setCustomPlatforms(updated);
                      updateSectionData('pos_integration', {
                        selectedPlatforms,
                        customPlatforms: updated,
                      });
                    }}
                    placeholder="e.g., Square, Clover, Lightspeed, etc."
                    className="max-w-md"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {selectedPlatforms.length > 0 && (
            <div className="flex justify-end">
              <Button onClick={handleContinueToConfiguration} className="gap-2">
                Continue to Configuration
                <CreditCard className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Progress indicator */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold">Platform Configuration</h3>
                  <p className="text-sm text-muted-foreground">
                    Platform {currentPlatformIndex + 1} of {selectedPlatforms.length}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleBackToSelection}>
                  Change Selection
                </Button>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${((currentPlatformIndex + 1) / selectedPlatforms.length) * 100}%` }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Current Platform Configuration */}
          {currentPlatformId && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {currentPlatform && currentPlatform.id !== 'other' && (
                      <img
                        src={currentPlatform.logo}
                        alt={currentPlatform.name}
                        className="h-6 w-6 object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                    {currentDisplayName}
                    {currentPlatform?.supported && (
                      <span className="text-xs text-muted-foreground">• Supported</span>
                    )}
                  </CardTitle>
                  <CardDescription>
                    Configure details for {currentDisplayName}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(() => {
                    const details = platformDetails[currentPlatformId] || {};
                    return (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor={`${currentPlatformId}-platform-type`}>Platform Type</Label>
                            <Select
                              value={details.platformType || ''}
                              onValueChange={(value) => {
                                setPlatformDetails(prev => ({
                                  ...prev,
                                  [currentPlatformId]: { ...prev[currentPlatformId], platformType: value }
                                }));
                                updateSectionData('pos_integration', {
                                  selectedPlatforms,
                                  customPlatforms,
                                  platformDetails: {
                                    ...platformDetails,
                                    [currentPlatformId]: { ...platformDetails[currentPlatformId], platformType: value }
                                  }
                                });
                              }}
                            >
                              <SelectTrigger id={`${currentPlatformId}-platform-type`}>
                                <SelectValue placeholder="Select platform type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="main_pos">Main POS</SelectItem>
                                <SelectItem value="3rd_party_delivery">3rd Party Delivery</SelectItem>
                                <SelectItem value="3rd_party_catering">3rd Party Catering Platform</SelectItem>
                                <SelectItem value="popups_events">Pop-ups & Events</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor={`${currentPlatformId}-payout-frequency`}>Payout Frequency</Label>
                            <Select
                              value={details.payoutFrequency || ''}
                              onValueChange={(value) => {
                                setPlatformDetails(prev => ({
                                  ...prev,
                                  [currentPlatformId]: { 
                                    ...prev[currentPlatformId], 
                                    payoutFrequency: value,
                                    payoutWeekday: value === 'weekly' ? prev[currentPlatformId]?.payoutWeekday : undefined
                                  }
                                }));
                                updateSectionData('pos_integration', {
                                  selectedPlatforms,
                                  customPlatforms,
                                  platformDetails: {
                                    ...platformDetails,
                                    [currentPlatformId]: { 
                                      ...platformDetails[currentPlatformId], 
                                      payoutFrequency: value,
                                      payoutWeekday: value === 'weekly' ? platformDetails[currentPlatformId]?.payoutWeekday : undefined
                                    }
                                  }
                                });
                              }}
                            >
                              <SelectTrigger id={`${currentPlatformId}-payout-frequency`}>
                                <SelectValue placeholder="Select frequency" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="daily">Daily</SelectItem>
                                <SelectItem value="weekly">Weekly</SelectItem>
                                <SelectItem value="bi_weekly">Bi-weekly</SelectItem>
                                <SelectItem value="monthly">Monthly</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {details.payoutFrequency === 'weekly' && (
                            <div className="space-y-2">
                              <Label htmlFor={`${currentPlatformId}-payout-weekday`}>Which weekday do deposits arrive? *</Label>
                              <Select
                                value={details.payoutWeekday || ''}
                                onValueChange={(value) => {
                                  setPlatformDetails(prev => ({
                                    ...prev,
                                    [currentPlatformId]: { ...prev[currentPlatformId], payoutWeekday: value }
                                  }));
                                  updateSectionData('pos_integration', {
                                    selectedPlatforms,
                                    customPlatforms,
                                    platformDetails: {
                                      ...platformDetails,
                                      [currentPlatformId]: { ...platformDetails[currentPlatformId], payoutWeekday: value }
                                    }
                                  });
                                }}
                              >
                                <SelectTrigger id={`${currentPlatformId}-payout-weekday`}>
                                  <SelectValue placeholder="Select weekday" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="monday">Monday</SelectItem>
                                  <SelectItem value="tuesday">Tuesday</SelectItem>
                                  <SelectItem value="wednesday">Wednesday</SelectItem>
                                  <SelectItem value="thursday">Thursday</SelectItem>
                                  <SelectItem value="friday">Friday</SelectItem>
                                  <SelectItem value="saturday">Saturday</SelectItem>
                                  <SelectItem value="sunday">Sunday</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Authorization Section for Current Platform */}
              <Card>
                <CardHeader>
                  <CardTitle>Integration Setup & Authorization</CardTitle>
                  <CardDescription>
                    Configure authorization for {currentDisplayName}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(() => {
                    const authSettings = authorizationSettings[currentPlatformId] || {};
                    return (
                      <>
                        {/* Display integration email only for Toast, DoorDash, and Uber Eats */}
                        {integrationEmails[currentPlatformId] && ['toast', 'doordash', 'ubereats'].includes(currentPlatformId) && (
                          <div className="mb-4 p-4 bg-muted rounded-lg border space-y-3">
                            <div>
                              <h5 className="font-semibold text-sm mb-1">Integration Email (Auto-Generated)</h5>
                              <p className="text-xs text-muted-foreground mb-3">
                                This email address is automatically generated for your company. Use it when setting up authorization or email forwarding in {currentDisplayName}.
                              </p>
                            </div>
                            <div className="flex items-center gap-2 p-3 bg-background border rounded-lg">
                              <code className="text-sm text-primary flex-1 font-mono break-all">
                                {integrationEmails[currentPlatformId]}
                              </code>
                              <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 flex-shrink-0"
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(integrationEmails[currentPlatformId]);
                                    toast({
                                      title: "Copied",
                                      description: `${currentDisplayName} integration email copied to clipboard.`,
                                    });
                                  } catch (error) {
                                    toast({
                                      title: "Copy failed",
                                      description: "Could not copy email to clipboard.",
                                      variant: "destructive",
                                    });
                                  }
                                }}
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Platform-specific access instructions */}
                        {currentPlatformId === 'doordash' && integrationEmails[currentPlatformId] && (
                          <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800 space-y-3">
                            <div>
                              <h5 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                DoorDash Setup Instructions
                              </h5>
                              <ol className="text-xs text-muted-foreground space-y-2 list-decimal list-inside">
                                <li>Log into DoorDash Merchant Portal</li>
                                <li>Navigate to Account Settings → Users</li>
                                <li>Add the integration email as a new user</li>
                                <li>DoorDash will send a One-Time Password (OTP) to your parent account email</li>
                                <li>Enter the OTP to complete setup</li>
                              </ol>
                            </div>
                          </div>
                        )}

                        {currentPlatformId === 'ubereats' && integrationEmails[currentPlatformId] && (
                          <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800 space-y-3">
                            <div>
                              <h5 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                Uber Eats Setup Instructions
                              </h5>
                              <ol className="text-xs text-muted-foreground space-y-2 list-decimal list-inside">
                                <li>Log into Uber Eats Manager</li>
                                <li>Navigate to Settings → Users</li>
                                <li>Invite the integration email as a new user</li>
                                <li>Uber will send an invite to the MenuBrain integration email</li>
                                <li>Check your Uber portal - the user should appear as "Pending Invite"</li>
                                <li>MenuBrain will automatically accept the invite</li>
                              </ol>
                            </div>
                          </div>
                        )}

                        {currentPlatformId === 'toast' && (
                          <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800 space-y-3">
                            <div>
                              <h5 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                Toast Setup Instructions
                              </h5>
                              <ol className="text-xs text-muted-foreground space-y-2 list-decimal list-inside">
                                <li>Log into Toast Web Portal</li>
                                <li>Navigate to Settings → API Access</li>
                                <li>Create new API credentials</li>
                                <li>Enter API Key and Secret below (optional - can be configured later)</li>
                              </ol>
                            </div>
                          </div>
                        )}

                        {/* Confirmation checkbox for platforms with integration emails */}
                        {['toast', 'doordash', 'ubereats'].includes(currentPlatformId) && integrationEmails[currentPlatformId] && (
                          <div className="mb-4 p-4 bg-muted rounded-lg border space-y-2">
                            <div className="flex items-start space-x-3">
                              <Checkbox
                                id={`${currentPlatformId}-integration-confirmed`}
                                checked={platformDetails[currentPlatformId]?.integrationConfirmed || false}
                                onCheckedChange={(checked) => {
                                  const isChecked = checked === true;
                                  setPlatformDetails(prev => ({
                                    ...prev,
                                    [currentPlatformId]: { ...prev[currentPlatformId], integrationConfirmed: isChecked }
                                  }));
                                  updateSectionData('pos_integration', {
                                    selectedPlatforms,
                                    customPlatforms,
                                    platformDetails: {
                                      ...platformDetails,
                                      [currentPlatformId]: { ...platformDetails[currentPlatformId], integrationConfirmed: isChecked }
                                    }
                                  });
                                }}
                              />
                              <div className="flex-1 space-y-1">
                                <Label
                                  htmlFor={`${currentPlatformId}-integration-confirmed`}
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                                >
                                  I have added the integration email to {currentDisplayName} and completed the authorization process
                                </Label>
                                <p className="text-xs text-muted-foreground">
                                  Please confirm that you have completed the setup steps above before continuing.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* CSV Headers Guide for manual upload platforms */}
                        {!['toast', 'doordash', 'ubereats'].includes(currentPlatformId) && currentPlatform?.supported && (
                          <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800 space-y-3">
                            <div>
                              <h5 className="font-semibold text-sm mb-1 flex items-center gap-2">
                                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                Manual CSV Upload Guide
                              </h5>
                              <p className="text-xs text-muted-foreground mb-3">
                                For {currentDisplayName}, you'll need to manually upload CSV files. When uploading, you'll be able to map your CSV headers to our database fields.
                              </p>
                            </div>
                            <div className="space-y-2">
                              <p className="text-xs font-medium text-foreground">Expected CSV Headers (you can use any column names - we'll help you map them):</p>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                                <div className="p-2 bg-background rounded border">
                                  <span className="font-mono">Order Date</span>
                                </div>
                                <div className="p-2 bg-background rounded border">
                                  <span className="font-mono">Sale Date</span>
                                </div>
                                <div className="p-2 bg-background rounded border">
                                  <span className="font-mono">Location</span>
                                </div>
                                <div className="p-2 bg-background rounded border">
                                  <span className="font-mono">Subtotal</span>
                                </div>
                                <div className="p-2 bg-background rounded border">
                                  <span className="font-mono">Tip Amount</span>
                                </div>
                                <div className="p-2 bg-background rounded border">
                                  <span className="font-mono">Commission</span>
                                </div>
                                <div className="p-2 bg-background rounded border">
                                  <span className="font-mono">Net Payout</span>
                                </div>
                                <div className="p-2 bg-background rounded border">
                                  <span className="font-mono">Gross Sales</span>
                                </div>
                                <div className="p-2 bg-background rounded border">
                                  <span className="font-mono">Transaction Fees</span>
                                </div>
                              </div>
                              <p className="text-xs text-muted-foreground mt-2">
                                <strong>Note:</strong> When you upload your CSV file, a matching modal will appear where you can map your column headers to these fields. Your column names don't need to match exactly - just select the correct mapping from the dropdown.
                              </p>
                            </div>
                          </div>
                        )}

                        <div className="space-y-2">
                          <Label>Data Retrieval Method</Label>
                          <div className="rounded-md border bg-muted/50 p-3">
                            <p className="text-sm text-muted-foreground">
                              Retrieval method is automatically determined by the backend based on platform API support.
                            </p>
                            {currentPlatform?.supported && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {currentPlatform.name} supports API-based integration.
                              </p>
                            )}
                          </div>
                        </div>

                        {currentPlatform?.supported && currentPlatformId === 'toast' && (
                          <div className="space-y-3 p-3 bg-muted rounded-lg">
                            <div className="space-y-2">
                              <Label htmlFor={`${currentPlatformId}-api-key`}>API Key (Optional)</Label>
                              <Input
                                id={`${currentPlatformId}-api-key`}
                                type="password"
                                value={authSettings.apiKey || ''}
                                onChange={(e) => {
                                  setAuthorizationSettings(prev => ({
                                    ...prev,
                                    [currentPlatformId]: { ...prev[currentPlatformId], apiKey: e.target.value }
                                  }));
                                  updateSectionData('pos_integration', {
                                    selectedPlatforms,
                                    customPlatforms,
                                    platformDetails,
                                    authorizationSettings: {
                                      ...authorizationSettings,
                                      [currentPlatformId]: { ...authorizationSettings[currentPlatformId], apiKey: e.target.value }
                                    }
                                  });
                                }}
                                placeholder="Enter API key"
                                className="max-w-md"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor={`${currentPlatformId}-api-secret`}>API Secret (Optional)</Label>
                              <Input
                                id={`${currentPlatformId}-api-secret`}
                                type="password"
                                value={authSettings.apiSecret || ''}
                                onChange={(e) => {
                                  setAuthorizationSettings(prev => ({
                                    ...prev,
                                    [currentPlatformId]: { ...prev[currentPlatformId], apiSecret: e.target.value }
                                  }));
                                  updateSectionData('pos_integration', {
                                    selectedPlatforms,
                                    customPlatforms,
                                    platformDetails,
                                    authorizationSettings: {
                                      ...authorizationSettings,
                                      [currentPlatformId]: { ...authorizationSettings[currentPlatformId], apiSecret: e.target.value }
                                    }
                                  });
                                }}
                                placeholder="Enter API secret"
                                className="max-w-md"
                              />
                            </div>
                            <p className="text-xs text-muted-foreground">
                              API credentials can also be configured later in Settings → Integrations
                            </p>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Navigation buttons */}
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={handleSkipPlatform}
                >
                  Skip This Platform
                </Button>
                <div className="flex gap-2">
                  {currentPlatformIndex > 0 && (
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPlatformIndex(currentPlatformIndex - 1)}
                    >
                      Previous
                    </Button>
                  )}
                  <Button onClick={handleNextPlatform}>
                    {currentPlatformIndex < selectedPlatforms.length - 1 ? 'Next Platform' : 'Complete Setup'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </SectionWrapper>
  );
};
